// The month that is period 1 of the fiscal year, as a 1-indexed calendar month.
// September, matching FISCAL_PERIOD_EXPR in lambda/billability_report_ibexiq/main.py,
// which is what writes the fiscalPeriod codes this class has to agree with.
export const DEFAULT_FISCAL_START = 9

// Actuals for a month don't land until part way into the following month; until then a
// period still reports work in hand rather than actuals.
export const ACTUALS_LAG_DAYS = 10


export default class FiscalCalendar {
    readonly fiscalYearStart: number;

    constructor(fiscalYearStart: number) {
        this.fiscalYearStart = fiscalYearStart; // 1–12
    }

    // 0-indexed month of period 1, which is what every conversion below pivots on.
    private get startMonthIndex(): number {
        return this.fiscalYearStart - 1;
    }

    // ---------- Date → Fiscal ----------

    // Periods are 1–12, so the fiscal start month is period 1 and the month before it is 12.
    getFiscalPeriod(date: Date): number {
        const month = date.getMonth(); // 0–11
        return ((month - this.startMonthIndex + 12) % 12) + 1;
    }

    getFiscalYear(date: Date): number {
        const year = date.getFullYear();
        return date.getMonth() >= this.startMonthIndex
            ? year + 1
            : year;
    }

    dateToFiscal(date: Date): { fiscalYear: number; period: number } {
        return {
            fiscalYear: this.getFiscalYear(date),
            period: this.getFiscalPeriod(date),
        };
    }

    // The earliest period still reporting work in hand: everything before it has closed to
    // actuals, it and everything after has not. Within the first ACTUALS_LAG_DAYS of a month
    // the previous month hasn't closed yet, so that month is still the first one on WIH.
    firstWorkInHandPeriod(date: Date = new Date()): { fiscalYear: number; period: number } {
        const d = new Date(date)
        if (d.getDate() < ACTUALS_LAG_DAYS) d.setMonth(d.getMonth() - 1)
        return this.dateToFiscal(d)
    }

    todayFiscalPeriod(): number {
        const { fiscalYear, period } = this.dateToFiscal(new Date())
        return fiscalYear * 100 + period
    }

    // The fiscal year today falls in (e.g. October 2026 -> 2027 with a September start).
    static currentFiscalYear(fiscalYearStart: number = DEFAULT_FISCAL_START): number {
        return new FiscalCalendar(fiscalYearStart).getFiscalYear(new Date())
    }

    // Reports always look back one month: in November they default to October's data.
    static lastMonth(fiscalYearStart: number = DEFAULT_FISCAL_START): { fiscalYear: number; period: number } {
        const d = new Date()
        d.setDate(1)
        d.setMonth(d.getMonth() - 1)
        return new FiscalCalendar(fiscalYearStart).dateToFiscal(d)
    }

    // Fiscal year of last month: the default year of every report filter
    static lastMonthFiscalYear(fiscalYearStart: number = DEFAULT_FISCAL_START): number {
        return FiscalCalendar.lastMonth(fiscalYearStart).fiscalYear
    }

    static getLastMonthPeriod(fiscalYearStart: number = DEFAULT_FISCAL_START): number {
        const calendar = new FiscalCalendar(fiscalYearStart)
        const d = new Date()
        d.setMonth(d.getMonth() - 1)
        const { fiscalYear, period } = calendar.dateToFiscal(d)
        return fiscalYear * 100 + period
    }

    // ---------- Fiscal → Date ----------

    fiscalToDate(
        fiscalYear: number,
        period: number
    ): Date {
        const monthIndex = (this.startMonthIndex + (period - 1)) % 12;

        // Periods from the fiscal start month to December sit in the previous calendar year.
        const year =
            monthIndex >= this.startMonthIndex
                ? fiscalYear - 1
                : fiscalYear;

        return new Date(year, monthIndex, 1);
    }

    // ---------- Display ----------

    fiscalToLabel(
        fiscalYear: number,
        period: number,
        locale = "en-CA"
    ): string {
        const date = this.fiscalToDate(fiscalYear, period);
        return date.toLocaleString(locale, {
            month: "short",
            year: "numeric",
        });
    }

    // ---------- Keys / Sorting ----------

    fiscalKey(fiscalYear: number, period: number): string {
        return `${fiscalYear}-${String(period).padStart(2, "0")}`;
    }

    parseFiscalKey(key: string): { fiscalYear: number; period: number } {
        const [year, period] = key.split("-").map(Number);
        return { fiscalYear: year, period };
    }


    keyToMonthString(key: number): string {
        const fiscalYear = Math.floor(key);
        const period = Math.round((key - fiscalYear) * 100);

        return this.fiscalToLabel(fiscalYear, period);
    }

    periodToMonth(period: number, locale = "en-CA", abreviated: boolean = false): string {
        if (period < 1 || period > 12) {
            throw new Error("Fiscal period must be between 1 and 12");
        }

        const monthIndex = (this.startMonthIndex + (period - 1)) % 12;

        return new Date(2000, monthIndex, 1).toLocaleString(locale, {
            month: abreviated ? 'short' : "long"
        })
    }

}
